/**
 * AssetPickerModal V2
 *
 * Modal with 3 tabs: Pool, Upload, Generate.
 * Role selector is shared across all tabs.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Image as ImageIcon, Upload, Sparkles, LayoutGrid } from 'lucide-react';
import type { AssetPoolItem } from '@/lib/api/products';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/v2/ui';
import { RoleSelector, type RoleOption } from './RoleSelector';
import { PoolAssetGrid } from './PoolAssetGrid';
import { AssetPickerUploadTab } from './AssetPickerUploadTab';
import { AssetPickerGenerateTab } from './AssetPickerGenerateTab';

// ============================================================================
// Types
// ============================================================================

export interface AssetPickerResult {
  type: 'pool' | 'upload' | 'generate';
  assetId?: string;
  file?: File;
  generatedImageId?: string;
  role?: string;
}

export interface AssetPickerModalProps {
  assets: AssetPoolItem[];
  roles: RoleOption[];
  onSelect: (result: AssetPickerResult) => void;
  onClose: () => void;
  slotNumber: number;
  productId: string;
}

// ============================================================================
// Component
// ============================================================================

export function AssetPickerModal({
  assets, roles, onSelect, onClose, slotNumber, productId,
}: AssetPickerModalProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('pool');
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>(
    roles[slotNumber - 1]?.value || '',
  );

  const handlePoolConfirm = () => {
    if (!selectedAsset) return;
    onSelect({ type: 'pool', assetId: selectedAsset, role: selectedRole || undefined });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Bild fuer Slot {slotNumber} waehlen
          </DialogTitle>
        </DialogHeader>

        <RoleSelector roles={roles} selected={selectedRole} onChange={setSelectedRole} />

        <Tabs defaultValue="pool" value={activeTab} onValueChange={setActiveTab} variant="pills">
          <TabsList className="w-full">
            <TabsTrigger value="pool" className="flex-1 gap-1.5">
              <LayoutGrid className="h-4 w-4" /> Pool
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex-1 gap-1.5">
              <Upload className="h-4 w-4" /> Upload
            </TabsTrigger>
            <TabsTrigger value="generate" className="flex-1 gap-1.5">
              <Sparkles className="h-4 w-4" /> Generieren
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pool">
            <PoolAssetGrid
              assets={assets}
              selectedAsset={selectedAsset}
              onSelectAsset={setSelectedAsset}
            />
          </TabsContent>

          <TabsContent value="upload">
            <AssetPickerUploadTab
              onSelect={(file) =>
                onSelect({ type: 'upload', file, role: selectedRole || undefined })
              }
            />
          </TabsContent>

          <TabsContent value="generate">
            <AssetPickerGenerateTab
              assets={assets}
              productId={productId}
              slotNumber={slotNumber}
              onSelect={(generatedImageId) =>
                onSelect({
                  type: 'generate',
                  generatedImageId,
                  role: selectedRole || undefined,
                })
              }
            />
          </TabsContent>
        </Tabs>

        {activeTab === 'pool' && (
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t('common.cancel')}</Button>
            </DialogClose>
            <Button variant="primary" disabled={!selectedAsset} onClick={handlePoolConfirm}>
              <Check className="h-4 w-4 mr-2" />
              Uebernehmen
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

